/**
 * Adapter Registry — INIT-2026-Q3-SCENE-HIERARCHY Work Package C.
 *
 * Single source of truth for which DomainAdapter handles which TreeNodeDomain.
 * All 8 domain adapters are auto-registered at module-init time; the
 * registerAdapter calls at the end of this file are the only side effects.
 *
 * Design constraints (OBJECT_IDENTITY_AND_SELECTION_POLICY §7, DESIGN_POLICY §5):
 *   - No `if (domain === "...")` branches in this file.
 *   - Domain identity flows entirely through `TreeNodeDomain` keys + adapter.detect().
 *   - `inferDomainFromSceneItem` uses only `layerKind`, `style.*`, `sourceCrs`,
 *     and `extensions.geosoft.*` — never a regex on `item.id` or `layerId`.
 *
 * Import order note: adapter modules are imported at the top (ES module
 * semantics guarantee they evaluate before this module's body). The
 * ADAPTER_REGISTRY Map is created first in the body; registerAdapter calls
 * follow, so no initialization-order hazard.
 */

import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type { TreeNodeDomain } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type { DomainAdapter } from "./DomainAdapter";

// Adapter imports — evaluated before this module's body per ES module spec.
// Adapters are plain exported objects with no dependency on this registry file,
// so there is no circular-import hazard.
import { IfcBuildingAdapter } from "./buildings/IfcBuildingAdapter";
import { CfihosPlantAdapter } from "./plant/CfihosPlantAdapter";
import { CimUtilityAdapter } from "./utilities/CimUtilityAdapter";
import { IfcInfraAdapter } from "./infrastructure/IfcInfraAdapter";
import { OsduSubseaAdapter } from "./subsea/OsduSubseaAdapter";
import { PointCloudAdapter } from "./survey/PointCloudAdapter";
import { CityGmlAdapter } from "./smartcity/CityGmlAdapter";
import { MilitaryAdeAdapter } from "./defence/MilitaryAdeAdapter";

// P3 runtime extraction adapters
import { TilesFeatureAdapter } from "./tiles/TilesFeatureAdapter";
import { GltfNodeAdapter } from "./gltf/GltfNodeAdapter";
import { PointCloudClassAdapter } from "./pointcloud/PointCloudClassAdapter";
import { ModelInternalsAdapter } from "./model/ModelInternalsAdapter";

// ─────────────────────────────────────────────────────────────────────────────
// § 1  Internal registry map
//
// Created first in the module body. All registerAdapter calls below run AFTER
// this line, so Map is always defined when adapters are inserted.
// ─────────────────────────────────────────────────────────────────────────────

const ADAPTER_REGISTRY = new Map<TreeNodeDomain, DomainAdapter>();

// ─────────────────────────────────────────────────────────────────────────────
// § 2  Registry operations
// ─────────────────────────────────────────────────────────────────────────────

// G8 close-out: the 3 real runtime extraction adapters (Source B) intentionally
// supersede the not-yet-implemented domain stubs that share their domain key —
// `TilesFeatureAdapter` over `CityGmlAdapter` ("smartcity") and
// `PointCloudClassAdapter` over `PointCloudAdapter` ("survey"). These overrides
// are by design (a real adapter must win over a stub); any OTHER same-domain
// overwrite is almost certainly an accident and is surfaced via a dev warning.
const INTENTIONAL_DOMAIN_OVERRIDES: ReadonlySet<TreeNodeDomain> = new Set([
  "smartcity",
  "survey",
  "building",
]);

/**
 * Registers an adapter. Overwrites any existing registration for the same domain
 * (last-write-wins). In DEV, an *unexpected* overwrite (not in
 * `INTENTIONAL_DOMAIN_OVERRIDES`) logs a warning so accidental collisions
 * surface during development. Exposed publicly so tests can register mocks.
 */
export function registerAdapter(adapter: DomainAdapter): void {
  if (
    import.meta.env?.DEV &&
    ADAPTER_REGISTRY.has(adapter.domain) &&
    !INTENTIONAL_DOMAIN_OVERRIDES.has(adapter.domain)
  ) {
     
    console.warn(
      `[scene-tree] adapter registry: '${adapter.domain}' overwritten by ` +
        `${adapter.standard ?? "?"} — unintended domain collision?`,
    );
  }
  ADAPTER_REGISTRY.set(adapter.domain, adapter);
}

/**
 * Returns the registered adapter for `domain`, or `null` if none is registered.
 */
export function getAdapter(domain: TreeNodeDomain): DomainAdapter | null {
  return ADAPTER_REGISTRY.get(domain) ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// § 3  Domain inference
//
// Rules (DESIGN_POLICY §5 — no domain-keyword regex on layerId):
//   - Explicit `extensions.geosoft.domain` wins over all heuristics.
//   - artifactKind / layerKind === "bim" | "ifc-glb"      → "building"
//   - artifactKind === "pmtiles" + style.tags ∋ "utility"  → "utility"
//   - layerKind === "cfihos"                                → "plant"  (G-21)
//   - layerKind ∈ {"copc","potree","pointcloud"}           → "survey"
//   - layerKind === "3dtiles" + Japan plane-rect CRS        → "smartcity"
//   - default                                              → "smartcity"
// ─────────────────────────────────────────────────────────────────────────────

type SceneItemWithExtensions = SceneItem & {
  extensions?: {
    geosoft?: {
      domain?: TreeNodeDomain;
      standard?: string;
    };
  };
};

/** Returns true when `crs` is one of Japan's plane rectangular coordinate systems (EPSG:6669–6687). */
function isJapanPlaneCrs(crs: string | undefined): boolean {
  if (!crs) return false;
  const m = crs.match(/^EPSG:(\d+)$/i);
  if (!m) return false;
  const n = parseInt(m[1], 10);
  return n >= 6669 && n <= 6687;
}

/**
 * Infers the `TreeNodeDomain` for a SceneItem using metadata heuristics.
 *
 * Never branches on `item.id` or `item.layerId` strings (DESIGN_POLICY §5).
 * All logic reads `layerKind`, `style.*`, `sourceCrs`, and the optional
 * `extensions.geosoft.*` extension fields.
 */
export function inferDomainFromSceneItem(item: SceneItem): TreeNodeDomain {
  const ext = (item as SceneItemWithExtensions).extensions?.geosoft;

  // Explicit domain annotation wins over all heuristics
  if (ext?.domain) return ext.domain;

  const style = item.style ?? {};
  const tags = (style["tags"] as string[] | undefined) ?? [];
  const artifactKind =
    (style["artifactKind"] as string | undefined) ?? item.layerKind;

  // building: BIM or IFC-GLB layer kind
  if (
    item.layerKind === "bim" ||
    item.layerKind === "ifc-glb" ||
    artifactKind === "bim" ||
    artifactKind === "ifc-glb"
  ) {
    return "building";
  }

  // utility: PMTiles with a "utility" tag
  if (
    (item.layerKind === "pmtiles" || artifactKind === "pmtiles") &&
    tags.includes("utility")
  ) {
    return "utility";
  }

  // plant: CFIHOS process-plant layers (G-21)
  if (
    item.layerKind === "cfihos" ||
    artifactKind === "cfihos"
  ) {
    return "plant";
  }

  // survey: point cloud formats
  if (
    item.layerKind === "copc" ||
    item.layerKind === "potree" ||
    item.layerKind === "pointcloud" ||
    artifactKind === "copc" ||
    artifactKind === "potree" ||
    artifactKind === "pointcloud"
  ) {
    return "survey";
  }

  // smartcity: 3D Tiles with Japan's plane rectangular CRS
  if (
    (item.layerKind === "3dtiles" || artifactKind === "3dtiles") &&
    isJapanPlaneCrs(item.sourceCrs)
  ) {
    return "smartcity";
  }

  // Default
  return "smartcity";
}

// ─────────────────────────────────────────────────────────────────────────────
// § 4  Adapter selection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Selects the best adapter for `item`:
 *   1. Infers domain via `inferDomainFromSceneItem`.
 *   2. Looks up the registered adapter for that domain.
 *   3. Asks the adapter to confirm it recognises the item via `adapter.detect(item)`.
 *
 * Returns `null` when no adapter is registered for the inferred domain, or
 * when the adapter's `detect` returns `false`.
 */
export function selectAdapterForSceneItem(item: SceneItem): DomainAdapter | null {
  const domain = inferDomainFromSceneItem(item);
  const adapter = getAdapter(domain);
  if (!adapter) return null;
  if (!adapter.detect(item)) return null;
  return adapter;
}

// ─────────────────────────────────────────────────────────────────────────────
// § 5  Auto-registration
//
// All 8 built-in adapters are registered here. This runs after ADAPTER_REGISTRY
// is created (sequential body execution) and after all adapter modules have
// evaluated (ES module depth-first evaluation).
// ─────────────────────────────────────────────────────────────────────────────

registerAdapter(IfcBuildingAdapter);
registerAdapter(CfihosPlantAdapter);
registerAdapter(CimUtilityAdapter);
registerAdapter(IfcInfraAdapter);
registerAdapter(OsduSubseaAdapter);
registerAdapter(PointCloudAdapter);
registerAdapter(CityGmlAdapter);
registerAdapter(MilitaryAdeAdapter);

// P3 runtime extraction adapters (Source B). TilesFeatureAdapter ("smartcity")
// and PointCloudClassAdapter ("survey") intentionally override the matching
// not-yet-implemented domain stubs above — a real runtime extractor must win
// over a stub. GltfNodeAdapter uses the distinct "generic" domain (no override).
// See INTENTIONAL_DOMAIN_OVERRIDES.
registerAdapter(TilesFeatureAdapter);
registerAdapter(GltfNodeAdapter);
registerAdapter(PointCloudClassAdapter);
registerAdapter(ModelInternalsAdapter);
